// Client-side sensitive-content mirror (typed help/intake framework, commit 2).
//
// A DELIBERATELY SMALL subset of the server matrix
// (apps/api/src/services/sensitiveContent.service.js), for one purpose: telling a
// reporter about an obvious problem while they are still typing, instead of after
// they press Send.
//
// It is NOT the gate. The server re-scans on prepare and on submit with the full
// matrix, and refuses the ticket regardless of what this module said. Two
// consequences the implementation follows from:
//
//   1. Only the unmistakable patterns live here. The full detector set — SSN
//      context windows, DoD ID proximity, patient/clinical pairing — stays server
//      side, because a browser bundle is readable, and shipping the complete
//      matrix publishes an evasion guide for the control it enforces.
//   2. Evidence is redacted here too. This module never puts a matched value into
//      component state, a log line, or an error message, so a mirrored warning
//      cannot become the leak it is warning about.

// Same shape the server returns, so one validation summary renders findings from
// either source: { detector, field, severity, redactedEvidence, message }.
const CLIENT_DETECTORS = Object.freeze([
  Object.freeze({
    detector: 'bearer_token',
    severity: 'block',
    pattern: /\bBearer\s+[A-Za-z0-9\-._~+/]{12,}={0,2}/i,
    message: 'Remove the access token — PRIME support never needs one to work a ticket.',
  }),
  Object.freeze({
    detector: 'password_assignment',
    severity: 'block',
    pattern: /\b(?:password|passwd|pwd|passphrase|secret)\s*[:=]\s*(\S+)/i,
    innocuousGroup: 1,
    message: 'Remove the password or secret — describe the sign-in failure instead.',
  }),
  Object.freeze({
    detector: 'ssn_formatted',
    severity: 'warn',
    pattern: /\b\d{3}[-\s]\d{2}[-\s]\d{4}\b/,
    message: 'That looks like a Social Security number. Remove it before sending.',
  }),
]);

// Mirrors the server's innocuous-value list closely enough that "password:
// expired" is not flagged here either. A reporter who sees a bogus warning
// learns to ignore all of them.
const INNOCUOUS_SECRET_VALUES = new Set([
  'expired',
  'incorrect',
  'invalid',
  'wrong',
  'unknown',
  'reset',
  'changed',
  'locked',
  'blank',
  'empty',
  'required',
  'rejected',
  'temporary',
  'help',
  'redacted',
  'removed',
  'none',
  'null',
]);

function isInnocuousSecretValue(value) {
  const cleaned = String(value ?? '')
    .trim()
    .replace(/^["'<[({]+|["'>\])}.,;:!?]+$/g, '')
    .toLowerCase();
  if (!cleaned) return true;
  if (INNOCUOUS_SECRET_VALUES.has(cleaned)) return true;
  return cleaned.length < 4 && /^[a-z]+$/.test(cleaned);
}

export function redactClientEvidence(match) {
  const masked = String(match ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[\p{L}\p{N}]/gu, '*');
  return masked.length > 32 ? `${masked.slice(0, 31)}…` : masked;
}

export function scanClientSensitiveContent(fields = {}) {
  const findings = [];
  for (const [field, value] of Object.entries(fields)) {
    if (typeof value !== 'string' || !value.trim()) continue;
    for (const detector of CLIENT_DETECTORS) {
      const match = detector.pattern.exec(value);
      if (!match) continue;
      if (
        detector.innocuousGroup != null &&
        isInnocuousSecretValue(match[detector.innocuousGroup])
      ) {
        continue;
      }
      findings.push({
        detector: detector.detector,
        field,
        severity: detector.severity,
        redactedEvidence: redactClientEvidence(match[0]),
        message: detector.message,
        // Marks the finding as advisory so the UI can say so plainly: the server
        // is what decides whether the ticket may be sent.
        source: 'client_preview',
      });
    }
  }
  return findings;
}

export function hasBlockingClientFinding(findings = []) {
  return findings.some((finding) => finding.severity === 'block');
}
